/**
 * createClosableStore(dbName, storeName)
 *
 * A drop-in replacement for idb-keyval v6's createStore(). The function it returns can be passed as the
 * `customStore` argument of idb-keyval's get / set / setMany / keys / clear, exactly like createStore's result.
 *
 * Why it exists: idb-keyval opens ONE IndexedDB connection and never closes it. That has two consequences:
 *  1. `indexedDB.deleteDatabase(name)` (e.g. a tester resetting state from the console) fires a `versionchange`
 *     event on our connection and then waits, "blocked", until the connection closes. idb-keyval never listens
 *     for that event, so the delete waits forever (until the tab is closed).
 *  2. Chrome refuses to put a page with an open IndexedDB connection into the back/forward cache, so pressing
 *     Back reloads the page instead of restoring it instantly (idb-keyval issue #166).
 *
 * What this does differently: it lets go of the connection
 *  - on `versionchange`  (someone called deleteDatabase(), or opened the DB asking for a higher version),
 *  - on `pagehide`       (the page is being navigated away from / hidden, so bfcache can take it),
 *  - on `close`          (Safari sometimes drops the connection by itself; idb-keyval already handled this).
 * After letting go, the very next get/set/... simply re-opens the connection. If the database was deleted in
 * between, `onupgradeneeded` recreates the object store, so the app keeps working with an empty store.
 *
 * Self-contained on purpose: it imports nothing from idb-keyval, because on the Vite/Vue 3 branches `shared/`
 * is its own npm package and idb-keyval is not among its dependencies.
 */

// Turns an IndexedDB request (or transaction) into a Promise. Same as idb-keyval's exported promisifyRequest.
function promisifyRequest (request) {
  return new Promise((resolve, reject) => {
    request.oncomplete = request.onsuccess = () => resolve(request.result)
    request.onabort = request.onerror = () => reject(request.error)
  })
}

export function createClosableStore (dbName, storeName) {
  let dbp     // the cached Promise<IDBDatabase> while a connection is open (or being opened), else undefined
  let openDb  // the live IDBDatabase once opened, so we can call close() on it synchronously, else undefined

  const forget = () => { dbp = undefined; openDb = undefined }
  // IDBDatabase.close() waits for in-flight transactions to finish before actually closing, so a save that is
  // mid-write when this fires still completes.
  const closeNow = () => { if (openDb) openDb.close(); forget() }

  const getDB = () => {
    if (dbp) return dbp
    const request = indexedDB.open(dbName)
    request.onupgradeneeded = () => request.result.createObjectStore(storeName)
    dbp = promisifyRequest(request)
    dbp.then(db => {
      openDb = db
      db.onversionchange = closeNow   // the actual fix: answer "please close" by closing
      db.onclose = forget             // connection vanished on its own: reopen lazily next time
    }, forget)                        // open failed: retry on the next call
    return dbp
  }

  if (typeof window !== 'undefined') window.addEventListener('pagehide', closeNow)

  return (txMode, callback) =>
    getDB().then(db => callback(db.transaction(storeName, txMode).objectStore(storeName)))
}
